/** Shared formatters and date parsers for the dashboard feature. */

import { parseISO } from 'date-fns';
import type { Project, InterimApplication } from '../../types/database';

export function fmtMoney(v: number): string {
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `£${Math.round(v / 1_000)}k`;
  if (v <= 0) return '£0';
  return `£${Math.round(v)}`;
}

export function safeParse(value?: string | Date | null): Date | null {
  if (!value) return null;
  let parsed: Date | null = null;
  // Date objects arrive from some hydrated entities (interim app fields, tasks).
  if (value instanceof Date) {
    parsed = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(trimmed)) {
      const [d, m, y] = trimmed.split(/[-/]/).map(Number);
      parsed = new Date(y, m - 1, d);
    } else if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      parsed = parseISO(trimmed);
    } else {
      parsed = new Date(trimmed);
    }
  }
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  // Guard against corrupt timestamps — e.g. a "0202-03-31" year typo in
  // interim_applications.period_end was rendering as "666266d over" in the
  // Action queue. A date outside a sane business window is treated as absent
  // rather than ancient/far-future, so one bad row can't poison the dashboard.
  const year = parsed.getFullYear();
  if (year < 2000 || year > 2100) return null;
  return parsed;
}

export interface ProjectCash {
  certified: number;
  paid: number;
  outstanding: number;
}

/**
 * Per-project certified / paid / outstanding, derived from interim applications
 * the same way FinancialTab does (certified ≈ the latest application's
 * cumulative-to-date; paid = the sum of paid applications' amountDue).
 *
 * Why not just read project.certifiedToDate / paidToDate? Those columns are
 * only ever written by a manual admin form — nothing rolls them up from the
 * interim-application workflow — so the dashboard cash surface stays blank even
 * when valuations exist. We derive from applications first and fall back to the
 * stored columns only when no application carries an amount, so manually-entered
 * figures still count.
 *
 * @param apps applications already filtered to this project.
 */
export function deriveProjectCash(project: Project, apps: InterimApplication[]): ProjectCash {
  const latest = apps
    .slice()
    .sort((a, b) => (b.applicationNumber ?? 0) - (a.applicationNumber ?? 0))[0];
  const certifiedFromApps = latest?.breakdown?.cumulativeToDate ?? 0;
  const paidFromApps = apps
    .filter((a) => a.status === 'paid')
    .reduce((sum, a) => sum + (a.summary?.amountDue ?? 0), 0);

  const certified = certifiedFromApps > 0 ? certifiedFromApps : project.certifiedToDate ?? 0;
  const paid = paidFromApps > 0 ? paidFromApps : project.paidToDate ?? 0;

  return { certified, paid, outstanding: Math.max(0, certified - paid) };
}
